import {
  AssignRoleItem,
  IKeyPersonEligibleEntity,
  useAssignRoles,
  useGetAttributeProperties,
} from '@commenda/api';
import { pluralize } from '@commenda/utils';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useNotification } from '../../../hooks/useNotification';
import { valueIsEmpty, type FieldDefinition } from '../CustomFieldsBuilder';
import { EntityRoleCard, RoleConfigSheet } from './components';
import { ASSIGN_RESULT_MESSAGE } from './constants';
import type {
  AddedRole,
  AssignRoleStepHandle,
  ConfigDraft,
  KeyPersonRoleType,
  SharedField,
} from './types';
import {
  addedRoleToItem,
  applyToAllDisabledReason,
  draftToAddedRoles,
  EMPTY_DRAFT,
  omitKey,
  toFieldKey,
} from './utils';

export type {
  AssignRoleStepHandle,
  KeyPersonRoleType,
} from './types';
export { EntityFlag } from './components';

// Owns the staged-role state and the assign call. Mounted only while the roles step is shown,
// so its state resets on unmount — no external reset needed.
export const AssignRoleStep = forwardRef<
  AssignRoleStepHandle,
  {
    keyPersonId: number;
    companyId: number;
    entities: IKeyPersonEligibleEntity[];
    onAssigned: () => void;
    onStateChange?: (s: { canSubmit: boolean; isSubmitting: boolean }) => void;
  }
>(({ keyPersonId, companyId, entities, onAssigned, onStateChange }, ref) => {
  const { setNotification } = useNotification();
  const assignMutation = useAssignRoles(keyPersonId, companyId);
  const { data: registry } = useGetAttributeProperties(companyId);

  const [addedRoles, setAddedRoles] = useState<AddedRole[]>([]);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  // Per-role failure reasons from the last submit, keyed by AddedRole.id.
  const [roleErrors, setRoleErrors] = useState<Record<string, string>>({});

  // Tracked apart from the target so the body survives the close slide-out.
  const [sheetOpen, setSheetOpen] = useState(false);
  const [configTarget, setConfigTarget] = useState<{
    corporationId: number;
    roleType: KeyPersonRoleType;
  } | null>(null);
  const [draft, setDraft] = useState<ConfigDraft>(EMPTY_DRAFT);
  // "Save for future" fields added this session, per attribute — shown on every form of
  // that role type and only written to the workspace on final submit (UI-only until then).
  const [sharedFields, setSharedFields] = useState<
    Record<string, SharedField[]>
  >({});

  const addSharedField = (attributeKey: string, def: FieldDefinition) => {
    const key = toFieldKey(def.label);
    setSharedFields((prev) => {
      const existing = prev[attributeKey] ?? [];
      if (existing.some((f) => f.key === key)) return prev;
      return {
        ...prev,
        [attributeKey]: [
          ...existing,
          {
            key,
            label: def.label.trim(),
            type: def.type,
            isRequired: def.isRequired,
          },
        ],
      };
    });
  };

  const removeSharedField = (attributeKey: string, key: string) => {
    setSharedFields((prev) => ({
      ...prev,
      [attributeKey]: (prev[attributeKey] ?? []).filter((f) => f.key !== key),
    }));
    // Drop the field's staged values so they aren't submitted as phantom property values.
    setAddedRoles((prev) =>
      prev.map((r) =>
        r.roleType === attributeKey
          ? { ...r, customValues: omitKey(r.customValues, key) }
          : r
      )
    );
    setDraft((prev) => ({
      ...prev,
      customValues: omitKey(prev.customValues, key),
    }));
  };

  // A staged role is incomplete when it lacks a value for a required shared field of its
  // attribute (e.g. one added after the role was staged) — flags it red and blocks submit.
  const incompleteRoleIds = useMemo(
    () =>
      new Set(
        addedRoles
          .filter((role) =>
            (sharedFields[role.roleType] ?? [])
              .filter((f) => f.isRequired)
              .some((f) => valueIsEmpty(f.type, role.customValues[f.key]))
          )
          .map((role) => role.id)
      ),
    [addedRoles, sharedFields]
  );

  const openConfig = (corporationId: number, roleType: KeyPersonRoleType) => {
    const base = EMPTY_DRAFT();
    if (roleType === 'GOVERNANCE') {
      const allowed =
        entities.find((e) => e.corporationId === corporationId)
          ?.allowedGovernanceRoles ?? [];
      const [onlyRole] = allowed;
      if (allowed.length === 1) base.governanceRole = onlyRole;
    }
    setDraft(base);
    setConfigTarget({ corporationId, roleType });
    setEditingRoleId(null);
    setSheetOpen(true);
  };

  const openEditConfig = (role: AddedRole) => {
    setDraft({
      startDate: role.startDate,
      endDate: role.endDate,
      officerRole: role.officerRole,
      roleEmail: role.roleEmail,
      governanceRole: role.governanceRole,
      controlType: role.controlType,
      applyToAll: false,
      customFields: role.customFields,
      customValues: role.customValues,
    });
    setConfigTarget({
      corporationId: role.corporationId,
      roleType: role.roleType,
    });
    setEditingRoleId(role.id);
    setSheetOpen(true);
  };

  const confirmAdd = () => {
    if (!configTarget) return;
    const { corporationId, roleType } = configTarget;
    if (editingRoleId) {
      const [updated] = draftToAddedRoles(draft, roleType, [corporationId]);
      setAddedRoles((prev) =>
        prev.map((r) =>
          r.id === editingRoleId ? { ...updated, id: editingRoleId } : r
        )
      );
      setRoleErrors((prev) => {
        const next = { ...prev };
        delete next[editingRoleId];
        return next;
      });
      setEditingRoleId(null);
      setSheetOpen(false);
      return;
    }
    let corporationIds = [corporationId];
    if (draft.applyToAll) {
      // Always include the originating entity; add the other selected ones that don't already
      // hold this role so the spread can't create duplicates.
      const already = new Set(
        addedRoles
          .filter((r) => r.roleType === roleType)
          .map((r) => r.corporationId)
      );
      corporationIds = [
        corporationId,
        ...entities
          .map((e) => e.corporationId)
          .filter((id) => id !== corporationId && !already.has(id)),
      ];
    }
    if (corporationIds.length > 0) {
      setAddedRoles((prev) => [
        ...prev,
        ...draftToAddedRoles(draft, roleType, corporationIds),
      ]);
    }
    setSheetOpen(false);
  };

  const removeRole = (id: string) => {
    setAddedRoles((prev) => prev.filter((r) => r.id !== id));
    setRoleErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleSubmit = async () => {
    const items: AssignRoleItem[] = addedRoles.map((role) =>
      addedRoleToItem(role, sharedFields[role.roleType] ?? [])
    );
    try {
      const { results } = await assignMutation.mutateAsync({ items });
      const created = results.filter((r) => r.status === 'created').length;
      // 'skipped' means the role already existed — not a failure.
      const failed = results.filter((r) => r.status === 'failed');
      if (failed.length === 0) {
        setNotification({
          show: true,
          valid: true,
          title: 'Roles assigned',
          message: `${pluralize(created, 'role assignment')} created.`,
        });
        onAssigned();
        return;
      }
      // Results are 1:1 with items in order; if that ever breaks, fall back to a generic
      // message rather than mis-attributing errors to the wrong role.
      const aligned = results.length === addedRoles.length;
      const errorsById: Record<string, string> = {};
      if (aligned) {
        addedRoles.forEach((role, i) => {
          const outcome = results[i];
          if (outcome?.status === 'failed') {
            errorsById[role.id] =
              ASSIGN_RESULT_MESSAGE[outcome.code ?? ''] ??
              outcome.code ??
              'could not be assigned';
          }
        });
        // Keep only the failed roles so the user can fix and retry them.
        setAddedRoles((prev) =>
          prev.filter((_, i) => results[i]?.status === 'failed')
        );
      }
      setRoleErrors(errorsById);
      setNotification({
        show: true,
        valid: false,
        title: 'Some roles were not assigned',
        message: `${created} assigned, ${pluralize(failed.length, 'role')} not assigned${aligned ? ' — see the flagged roles below' : ''}.`,
      });
    } catch (error) {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to assign roles',
        message: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  useImperativeHandle(ref, () => ({ submit: handleSubmit }));

  const canSubmit =
    addedRoles.length > 0 &&
    incompleteRoleIds.size === 0 &&
    !assignMutation.isPending;
  const isSubmitting = assignMutation.isPending;

  useEffect(() => {
    onStateChange?.({ canSubmit, isSubmitting });
  }, [canSubmit, isSubmitting, onStateChange]);

  const configEntity = entities.find(
    (e) => e.corporationId === configTarget?.corporationId
  );
  const configEntityName = configEntity?.corporationName ?? 'entity';

  return (
    <>
      <div className="flex flex-col gap-2xl">
        {entities.map((entity) => (
          <EntityRoleCard
            key={entity.corporationId}
            entityName={
              entity.corporationName ?? `Entity #${entity.corporationId}`
            }
            country={entity.country}
            addedRoles={addedRoles.filter(
              (r) => r.corporationId === entity.corporationId
            )}
            onAddRole={(roleType) => openConfig(entity.corporationId, roleType)}
            onRemoveRole={removeRole}
            onEditRole={openEditConfig}
            roleErrors={roleErrors}
            incompleteIds={incompleteRoleIds}
            registry={registry}
            governanceDisabled={entity.allowedGovernanceRoles.length === 0}
            hideGovernance={entity.isGovernanceRoleApplicable === false}
          />
        ))}
      </div>

      <RoleConfigSheet
        open={sheetOpen}
        roleType={configTarget?.roleType ?? null}
        entityName={configEntityName}
        selectedCount={entities.length}
        isEditing={editingRoleId != null}
        companyId={companyId}
        draft={draft}
        onPatch={(patch) => setDraft((prev) => ({ ...prev, ...patch }))}
        onClose={() => {
          setSheetOpen(false);
          setEditingRoleId(null);
        }}
        onAdd={confirmAdd}
        sharedFields={
          configTarget ? sharedFields[configTarget.roleType] ?? [] : []
        }
        onAddSharedField={(def) => {
          if (configTarget) addSharedField(configTarget.roleType, def);
        }}
        onRemoveSharedField={(key) => {
          if (configTarget) removeSharedField(configTarget.roleType, key);
        }}
        allowedGovernanceRoles={configEntity?.allowedGovernanceRoles ?? []}
        governanceStructure={configEntity?.governanceStructure}
        governanceSeatRole={configEntity?.governanceSeatRole}
        applyToAllDisabledReason={
          configTarget
            ? applyToAllDisabledReason(configTarget.roleType, entities)
            : null
        }
      />
    </>
  );
});

AssignRoleStep.displayName = 'AssignRoleStep';
